import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  getSafeDistanceMiles,
  normalizeCoordinates,
  normalizeProfileLocation,
} from "../../utils/locationUtils";
import CreateCommunityPost, { COMMUNITY_TYPES } from "./CreateCommunityPost";
import CommunityPostCard from "./CommunityPostCard";
import {
  getExchangeTypeLabel,
  getWantedCategoryLabel,
  normalizeBarterFields,
} from "../../utils/barter";
import {
  getHydratedCommentCount,
  getPostsNeedingCommentCountHydration,
  mergeCommentCount,
} from "../../utils/communityCommentCounts";

const RADIUS_OPTIONS = [
  { value: "25", label: "25 miles" },
  { value: "50", label: "50 miles" },
  { value: "100", label: "100 miles" },
  { value: "250", label: "250 miles" },
  { value: "anywhere", label: "Anywhere" },
];

const getUserCoordinates = (userLocation, userProfile) => {
  return normalizeCoordinates(userLocation) || normalizeCoordinates(normalizeProfileLocation(userProfile));
};

const getPostDistance = (post, userCoordinates) => {
  if (!userCoordinates) return null;
  return getSafeDistanceMiles(userCoordinates, post);
};

const normalize = (value) => String(value || "").toLowerCase().trim();

const isOfficialPinnedPost = (post = {}) =>
  post.pinned === true &&
  (post.displayAsBrand === true ||
    post.isFounderPost === true ||
    Boolean(post.createdByAdminId) ||
    (post.authorName === "MyFarmingPal" && Boolean(post.createdByAdminId)));

const CommunityFeed = ({ currentUser, userProfile, userLocation }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [directPost, setDirectPost] = useState(null);
  const [commentCountsByPostId, setCommentCountsByPostId] = useState({});
  const [loading, setLoading] = useState(true);
  const [radius, setRadius] = useState("100");
  const [typeFilter, setTypeFilter] = useState(location.state?.typeFilter || "all");
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [showPostForm, setShowPostForm] = useState(Boolean(location.state?.openPostForm));
  const [showFilters, setShowFilters] = useState(Boolean(location.state?.typeFilter));
  const directPostId = useMemo(
    () => new URLSearchParams(location.search || "").get("post") || "",
    [location.search]
  );
  const directPostFilterAppliedRef = useRef("");
  const directPostFocusedRef = useRef("");

  const userCoordinates = useMemo(
    () => getUserCoordinates(userLocation, userProfile),
    [userLocation, userProfile]
  );

  useEffect(() => {
    const postsQuery = query(
      collection(db, "communityPosts"),
      where("status", "==", "active"),
      orderBy("createdAt", "desc"),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      postsQuery,
      (snapshot) => {
        const nextPosts = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        setPosts(nextPosts);
        setLoading(false);
      },
      (error) => {
        console.error("Community posts listener failed:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!directPostId || posts.some((post) => post.id === directPostId)) {
      setDirectPost(null);
      return;
    }

    let cancelled = false;

    getDoc(doc(db, "communityPosts", directPostId))
      .then((docSnap) => {
        if (cancelled) return;
        if (!docSnap.exists()) {
          setDirectPost(null);
          return;
        }

        const post = { id: docSnap.id, ...docSnap.data() };
        if (
          post.isArchived === true ||
          post.isHidden === true ||
          (post.status || "active") !== "active"
        ) {
          setDirectPost(null);
          return;
        }

        setDirectPost(post);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Community direct post lookup failed:", error);
          setDirectPost(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [directPostId, posts]);

  const feedPosts = useMemo(() => {
    if (!directPost || posts.some((post) => post.id === directPost.id)) {
      return posts;
    }

    return [directPost, ...posts];
  }, [directPost, posts]);

  useEffect(() => {
    if (!directPostId || directPostFilterAppliedRef.current === directPostId) return;

    const targetPost = feedPosts.find((post) => post.id === directPostId);
    if (
      !targetPost ||
      targetPost.isArchived === true ||
      targetPost.isHidden === true ||
      (targetPost.status || "active") !== "active"
    ) {
      return;
    }

    directPostFilterAppliedRef.current = directPostId;
    setTypeFilter("all");
    setRadius("anywhere");
    setSearch("");
    setShowFilters(true);
  }, [directPostId, feedPosts]);

  const filteredPosts = useMemo(() => {
    const searchTerm = normalize(search);
    const selectedRadius = radius === "anywhere" ? null : Number(radius);

    const visiblePosts = feedPosts
      .filter((post) => post.isArchived !== true)
      .filter((post) => post.isHidden !== true)
      .filter((post) => (post.status || "active") === "active")
      .filter((post) => {
        if (typeFilter === "all") return true;
        if (typeFilter === "regular_posts") {
          return post.postType !== "want_ad" && post.type !== "want_ad";
        }
        if (typeFilter === "want_ad") {
          return post.postType === "want_ad" || post.type === "want_ad";
        }
        return post.type === typeFilter;
      })
      .filter((post) => {
        if (!searchTerm) return true;
        const barter = normalizeBarterFields(post);

        return normalize(
          [
            post.title,
            post.text,
            post.wantedTitle,
            post.wantedDescription,
            post.postType === "want_ad" || post.type === "want_ad"
              ? "looking for want ad iso wanted"
              : "",
            getWantedCategoryLabel(post.wantedCategory),
            getExchangeTypeLabel(barter.exchangeType),
            barter.barterOpenToAnything ? "open to any fair trade" : "",
            Array.isArray(barter.barterCategories) ? barter.barterCategories.join(" ") : "",
            barter.barterNotes,
            post.budgetText,
            post.urgency,
            post.city,
            post.state,
            post.county,
            post.locationText,
            post.locationNote,
            post.authorName,
            post.type === "land_use" ? "land use pasture grazing fenced garden field access" : "",
            post.landUseType,
            post.offeringOrSeeking,
            post.animalAllowedNotes,
            post.pinned ? "pinned" : "",
            post.isFounderPost ? "founder admin" : "",
            isOfficialPinnedPost(post) ? "official myfarmingpal team" : "",
          ].join(" ")
        ).includes(searchTerm);
      })
      .map((post) => ({
        ...post,
        distanceMiles: isOfficialPinnedPost(post)
          ? null
          : getPostDistance(post, userCoordinates),
      }))
      .filter((post) => {
        if (isOfficialPinnedPost(post)) return true;
        if (radius === "anywhere") return true;
        if (!userCoordinates) return true;
        if (typeof post.distanceMiles === "number") {
          return post.distanceMiles <= selectedRadius;
        }

        return Boolean(searchTerm || typeFilter !== "all");
      });

    return visiblePosts.sort((a, b) => {
      if (Boolean(a.pinned) !== Boolean(b.pinned)) {
        return a.pinned ? -1 : 1;
      }

      if (sortOrder === "newest") {
        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      }

      return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
    });
  }, [feedPosts, radius, search, sortOrder, typeFilter, userCoordinates]);

  useEffect(() => {
    const missingPostIds = getPostsNeedingCommentCountHydration(
      filteredPosts,
      commentCountsByPostId
    );

    if (missingPostIds.length === 0) return;

    let cancelled = false;

    Promise.all(
      missingPostIds.map(async (postId) => {
        const commentsQuery = query(
          collection(db, "communityPosts", postId, "comments"),
          where("status", "==", "active")
        );
        const countSnapshot = await getCountFromServer(commentsQuery);

        return [postId, countSnapshot.data().count || 0];
      })
    )
      .then((entries) => {
        if (cancelled) return;

        setCommentCountsByPostId((current) => {
          const next = { ...current };
          entries.forEach(([postId, count]) => {
            next[postId] = Math.max(0, Number(count) || 0);
          });
          return next;
        });
      })
      .catch((error) => {
        console.error("Community comment count hydration failed:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [commentCountsByPostId, filteredPosts]);

  useEffect(() => {
    if (!directPostId || loading || directPostFocusedRef.current === directPostId) return;
    if (!filteredPosts.some((post) => post.id === directPostId)) return;

    const targetElement = document.getElementById(`community-post-${directPostId}`);
    if (!targetElement) return;

    directPostFocusedRef.current = directPostId;
    targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
    targetElement.focus({ preventScroll: true });
  }, [directPostId, filteredPosts, loading]);

  const emptyTitle = search.trim()
    ? "No posts matched that search."
    : typeFilter === "land_use"
    ? "No land use posts yet."
    : typeFilter === "want_ad"
    ? "No Want-Ads nearby yet."
    : "No community posts nearby yet 🌾";

  const emptyText = search.trim()
    ? "Try a different keyword, category, or location."
    : typeFilter === "land_use"
    ? "Offer or find pasture, grazing, garden plots, or fenced space nearby."
    : typeFilter === "want_ad"
    ? "Post what you are looking for so nearby people can reply."
    : "Introduce yourself, ask a local question, or widen your radius to find more neighbors.";

  return (
    <div className="community-page page-shell page-shell-wide">
      <section className="community-hero">
        <div>
          <p className="eyebrow">Local community board</p>
          <h2>Community</h2>
          <p>
            Ask questions, share local farm updates, find recommendations, and
            connect with people nearby.
          </p>
        </div>
        <div className="community-hero-note">
          No hidden ranking. Use distance, category, newest posts, and helpful local replies.
        </div>
      </section>

      <div className="community-desktop-layout">
        <aside className="community-control-panel">
          <button
            type="button"
            className="community-toggle-post-btn"
            onClick={() => setShowPostForm((current) => !current)}
          >
            {showPostForm ? "− Hide post form" : "+ Share a local post"}
          </button>

          {showPostForm && (
            <CreateCommunityPost
              currentUser={currentUser}
              userProfile={userProfile}
              userLocation={userLocation}
              initialType={
                typeFilter === "land_use"
                  ? "land_use"
                  : typeFilter === "want_ad"
                  ? "want_ad"
                  : "general"
              }
            />
          )}

          <button
            type="button"
            className="community-filters-toggle-btn"
            onClick={() => setShowFilters((current) => !current)}
          >
            {showFilters ? "Filters ▴" : "Filters ▾"}
          </button>

          {showFilters && (
            <section className="community-filter-card">
              <label className="form-label">
                Radius
                <select value={radius} onChange={(event) => setRadius(event.target.value)}>
                  {RADIUS_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn-soft location-change-link"
                  onClick={() => navigate("/edit-profile#profile-location-section")}
                >
                  Change location
                </button>
              </label>

              <label className="form-label">
                Category
                <select
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                >
                  <option value="all">All</option>
                  <option value="regular_posts">Regular Posts</option>
                  <option value="want_ad">Looking For / Want-Ads</option>
                  {COMMUNITY_TYPES.filter((item) => item.value !== "want_ad").map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="form-label">
                Sort
                <select
                  value={sortOrder}
                  onChange={(event) => setSortOrder(event.target.value)}
                >
                  <option value="newest">Newest</option>
                </select>
              </label>

              <label className="form-label community-search-field">
                Search
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search title, text, location..."
                />
              </label>
            </section>
          )}
        </aside>

        <main className="community-feed-panel">
          {loading ? (
            <div className="community-empty-state">Loading community posts...</div>
          ) : filteredPosts.length === 0 ? (
            <div className="community-empty-state">
              <strong>{emptyTitle}</strong>
              <p>{emptyText}</p>
              <div className="empty-state-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => setShowPostForm(true)}
                >
                  Share a Post
                </button>
                <button
                  type="button"
                  className="btn-soft"
                  onClick={() => {
                    setRadius("anywhere");
                    setShowFilters(true);
                  }}
                >
                  Widen Distance
                </button>
              </div>
            </div>
          ) : (
            <section className="community-post-list">
              {filteredPosts.map((post) => (
                <CommunityPostCard
                  key={post.id}
                  post={post}
                  currentUser={currentUser}
                  userProfile={userProfile}
                  distanceMiles={post.distanceMiles}
                  isDirectTarget={post.id === directPostId}
                  commentCountOverride={getHydratedCommentCount(
                    post,
                    commentCountsByPostId
                  )}
                  commentCountLoading={
                    getHydratedCommentCount(post, commentCountsByPostId) === null
                  }
                  onCommentCountChange={(count) => {
                    setCommentCountsByPostId((current) =>
                      mergeCommentCount(current, post.id, count)
                    );
                  }}
                />
              ))}
            </section>
          )}
        </main>
      </div>
    </div>
  );
};

export default CommunityFeed;
